import {
  ConfigType,
  DeliveryLocation,
  DeliveryLocationsByCustomer,
  DisplayHostnameConfig,
  HostnameConfig,
  LocationCustomer,
  UserInfo,
} from "./types";

// Mirrors hostnameConfigController.js's determineHostnameConfigType().
export function determineConfigType(config: HostnameConfig): ConfigType {
  if (config.hostname === "bbot.menu") return "hostedByBbot";
  if (config.redirect_to_hostname_config) return "redirectToHostname";
  if (config.is_legacy) {
    return config.legacy_properties?.theme_id === "basic_delivery_theme" ? "basicDeliveryTheme" : "preBuilt";
  }
  return "bosBuilder";
}

// Mirrors hostnameConfigController.js's determineRedirectString().
export function determineRedirectString(config: HostnameConfig): string {
  if (config.redirect_to_hostname_config) {
    return `redirects to ${config.redirect_to_hostname_config.hostname}`;
  }
  if (config.redirect_to_delivery_location) {
    return `redirects to table code ${config.redirect_to_delivery_location.locationShortId}`;
  }
  return "";
}

// Mirrors hostnameConfigController.js's userIsAllowedToEdit(). If the server already
// says not-editable, that's final. Otherwise every configType except bosBuilder is
// editable by anyone; bosBuilder specifically excludes an admin who isn't on the bbot
// team, while still allowing plain owners and bbot-team admins. This asymmetry is
// intentional legacy behavior, not a simplification opportunity.
export function userIsAllowedToEdit(config: HostnameConfig, configType: ConfigType, userInfo: UserInfo): boolean {
  if (!config.is_editable) return false;
  const isAdmin = userInfo.role === "admin";
  const isBbotAdmin = Boolean(userInfo.teams?.includes("bbot"));
  return isBbotAdmin || configType !== "bosBuilder" || (!isAdmin && configType === "bosBuilder");
}

export function toDisplayConfig(config: HostnameConfig, userInfo: UserInfo): DisplayHostnameConfig {
  const configType = determineConfigType(config);
  return {
    ...config,
    configType,
    hostnameAliasesString: config.hostname_aliases?.join(", ") ?? "",
    redirectString: determineRedirectString(config),
    canEdit: userIsAllowedToEdit(config, configType, userInfo),
  };
}

// Mirrors getAllHostnameConfigs()'s placeholder rows for hostnames the customer owns
// but that have no HostnameConfig yet. configType is a literal here, not derived via
// determineConfigType(), which has no notConfigured case and would fall through to
// its bosBuilder default - legacy never runs these placeholders through that function.
export function toNotConfiguredDisplayConfig(hostname: string, userInfo: UserInfo): DisplayHostnameConfig {
  const config: HostnameConfig = { hostname, is_editable: true, show_account_sign_in: true, show_bbot_footer: true };
  return {
    ...config,
    configType: "notConfigured",
    hostnameAliasesString: "",
    redirectString: "",
    canEdit: userIsAllowedToEdit(config, "notConfigured", userInfo),
  };
}

// Strips the client-only derived fields before sending a config back to
// POST /owner/editHostnameConfig, which expects the raw serializer shape.
export function toRawHostnameConfig(config: DisplayHostnameConfig): HostnameConfig {
  const { configType, hostnameAliasesString, redirectString, canEdit, ...raw } = config;
  return raw;
}

export const isAdminUser = (userInfo?: UserInfo): boolean => userInfo?.role === "admin";
export const isBbotAdminUser = (userInfo?: UserInfo): boolean => Boolean(userInfo?.teams?.includes("bbot"));

const RESTRICTED_HOSTNAME_PREFIXES = ["old.", "app.", "www.", "http://"];
const HOSTNAME_FORMAT = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,8}[a-z0-9]$/;
export const ENVS_REQUIRING_HOSTNAME_PREFIX = ["demo", "dev", "staging"];

// Mirrors addNewHostnameController's validateHostname().
export function isValidHostname(hostname: string, env: string): boolean {
  if (ENVS_REQUIRING_HOSTNAME_PREFIX.includes(env) && !hostname.startsWith(env + ".")) return false;
  if (RESTRICTED_HOSTNAME_PREFIXES.some((prefix) => hostname.startsWith(prefix))) return false;
  if (/\s/.test(hostname)) return false;
  return HOSTNAME_FORMAT.test(hostname);
}

export type SwitchTextChoice = "default" | "remove" | "custom";

// Mirrors enableBasicDeliveryThemeController/enableWhiteLabelBuilderController's
// determineSwitchTextChoice().
export function determineSwitchTextChoice(switchText: string | undefined): SwitchTextChoice {
  if (switchText === undefined || switchText === "Switch Location") return "default";
  if (!switchText) return "remove";
  return "custom";
}

// Mirrors those same controllers' switchTextChoiceSelected(): picking "default"
// resets the text; picking "remove" or "custom" both start from blank (custom
// then lets the owner type their own text into the same field).
export function switchTextForChoice(choice: SwitchTextChoice): string {
  return choice === "default" ? "Switch Location" : "";
}

// Location-assignment subsystem ("Choose Locations Accessible From {hostname}").
// Mirrors hostnameConfigController.js's editSelectedConfig()/preselectCustomersWithHostname()/
// canSaveHostnameToLocations()/updateLocationFilter()/checkLocationsForHostname().

export const allLocations = (locationsByCustomer: DeliveryLocationsByCustomer): DeliveryLocation[] =>
  Object.values(locationsByCustomer).flat();

// Mirrors editSelectedConfig()'s location.showOnHostname = location.hostnames.includes(hostname).
export function initialCheckedLocationIds(
  locationsByCustomer: DeliveryLocationsByCustomer,
  hostname: string
): Set<string> {
  const checked = new Set<string>();
  allLocations(locationsByCustomer).forEach((location) => {
    if (location.hostnames.includes(hostname)) checked.add(location.locationId);
  });
  return checked;
}

// Mirrors preselectCustomersWithHostname(): the read-only display card's grouping.
// Omits customers with zero locations currently on this hostname, same as legacy's
// own ng-if="customerLocations.length > 0".
export function groupLocationsByCustomerOnHostname(
  locationsByCustomer: DeliveryLocationsByCustomer,
  checkedLocationIds: Set<string>
): DeliveryLocationsByCustomer {
  const result: DeliveryLocationsByCustomer = {};
  Object.entries(locationsByCustomer).forEach(([customerId, locations]) => {
    const onHostname = locations.filter((location) => checkedLocationIds.has(location.locationId));
    if (onHostname.length > 0) result[customerId] = onHostname;
  });
  return result;
}

export const customersById = (customers: LocationCustomer[]): Record<string, LocationCustomer> =>
  Object.fromEntries(customers.map((customer) => [customer.customer_id, customer]));

// Mirrors canSaveHostnameToLocations()'s duplicateLocationsError: computed across every
// connected customer's locations, not just whichever one is currently filtered. Takes an
// already-flattened list (see LocationAssignmentSection's own flatLocations memo) rather
// than re-flattening locationsByCustomer itself - this runs on every checkbox toggle.
export function findDuplicateCheckedShortIds(locations: DeliveryLocation[], checkedLocationIds: Set<string>): boolean {
  const checkedShortIds = locations
    .filter((location) => checkedLocationIds.has(location.locationId))
    .map((location) => location.locationShortId);
  return checkedShortIds.length !== new Set(checkedShortIds).size;
}

// Mirrors canSaveHostnameToLocations()'s uneditableLocations: a location that's been
// unchecked here but whose *only* hostname association is this one would be orphaned
// (zero remaining hostnames) if saved - hard-blocks Save, same as legacy. Takes an
// already-flattened list for the same reason as findDuplicateCheckedShortIds above.
export function findUneditableLocationShortIds(
  locations: DeliveryLocation[],
  checkedLocationIds: Set<string>,
  hostname: string
): string[] {
  return locations
    .filter(
      (location) =>
        !checkedLocationIds.has(location.locationId) &&
        location.hostnames.length === 1 &&
        location.hostnames.includes(hostname)
    )
    .map((location) => location.locationShortId);
}

// Mirrors updateLocationFilter(). Only ever applied to the currently-selected
// customer's locations, since that's the only list the edit checklist renders.
export function filterLocationsForChecklist(
  locations: DeliveryLocation[],
  searchText: string,
  chosenFulfillmentMethods: string[]
): DeliveryLocation[] {
  const formattedQuery = searchText.toLowerCase().replace(/\s/g, "");
  return locations.filter((location) => {
    const passesFulfillmentFilter = chosenFulfillmentMethods.includes(location.fulfillment_method);
    if (!formattedQuery) return passesFulfillmentFilter;
    const passesTextFilter = location.locationShortId.toLowerCase().replace(/\s/g, "").includes(formattedQuery);
    return passesTextFilter && passesFulfillmentFilter;
  });
}

// Mirrors checkLocationsForHostname(): a bulk-paste check that can reach locations
// belonging to any connected customer, not just whichever one is currently selected.
export function locationIdsMatchingShortIds(
  locationsByCustomer: DeliveryLocationsByCustomer,
  shortIdsToCheck: string[]
): string[] {
  return allLocations(locationsByCustomer)
    .filter((location) => shortIdsToCheck.includes(location.locationShortId))
    .map((location) => location.locationId);
}
